import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import Login from "./components/Auth/Login";
import ResetPassword from "./components/Auth/ResetPassword";
import Profile from "./components/Auth/Profile";
import Dashboard from "./components/Dashboard/Dashboard";
import AdminDashboard from "./components/Admin/AdminDashboard";
import SampleLookupPage from "./components/Admin/SampleLookupPage";
import Header from "./components/Layout/Header";
import UnderConstructionSplash from "./components/UnderConstructionSplash";
import { getCurrentUser } from "./services/api";

// ── Auth context ─────────────────────────────────────────────

const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const stored = localStorage.getItem("sdx_user");
    if (stored) {
      try { setUser(JSON.parse(stored)); } catch { localStorage.clear(); }
    }
    getCurrentUser()
      .then(res => {
        if (res.data?.user) {
          localStorage.setItem("sdx_user", JSON.stringify(res.data.user));
          setUser(res.data.user);
        }
      })
      .catch(() => {
        localStorage.removeItem("sdx_user");
        setUser(null);
      })
      .finally(() => setLoading(false));
  }, []);

  const loginUser = useCallback((userData) => {
    localStorage.setItem("sdx_user", JSON.stringify(userData));
    setUser(userData);
  }, []);

  const logoutUser = useCallback(() => {
    localStorage.removeItem("sdx_user");
    setUser(null);
  }, []);

  const updateUser = useCallback((userData) => {
    setUser((current) => {
      const nextUser = { ...(current || {}), ...(userData || {}) };
      localStorage.setItem("sdx_user", JSON.stringify(nextUser));
      return nextUser;
    });
  }, []);

  if (loading) return null;

  return (
    <AuthContext.Provider value={{ user, loginUser, logoutUser, updateUser }}>
      {children}
    </AuthContext.Provider>
  );
}

// ── Theme context ─────────────────────────────────────────────

const ThemeContext = createContext({ isDark: false, toggle: () => {} });
export const useTheme = () => useContext(ThemeContext);

function ThemeProvider({ children }) {
  const [isDark, setIsDark] = useState(() => localStorage.getItem("sdx-theme") === "dark");

  const toggle = () => setIsDark(prev => {
    const next = !prev;
    localStorage.setItem("sdx-theme", next ? "dark" : "light");
    return next;
  });

  return (
    <ThemeContext.Provider value={{ isDark, toggle }}>
      {children}
    </ThemeContext.Provider>
  );
}

// ── Route guards ──────────────────────────────────────────────

function isAdminRole(role) {
  return role === "admin" || role === "collaborator";
}

function ProtectedRoute({ children }) {
  const { user } = useAuth();
  return user ? children : <Navigate to="/login" replace />;
}

function PublicRoute({ children }) {
  const { user } = useAuth();
  const location = useLocation();
  const switchingAccount = new URLSearchParams(location.search).get("switchAccount") === "1";
  if (!user || switchingAccount) return children;
  if (isAdminRole(user.role)) {
    return <Navigate to="/admin" replace />;
  }
  return <Navigate to="/" replace />;
}

function AdminSampleLookupRoute({ children }) {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  return isAdminRole(user.role) ? children : <Navigate to="/dashboard" replace />;
}

function AdminRoute({ children }) {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  return isAdminRole(user.role) ? children : <Navigate to="/dashboard" replace />;
}

// ── App shell — reads both contexts ──────────────────────────

function AppShell() {
  const { isDark } = useTheme();
  return (
    <div
      data-theme={isDark ? "dark" : "light"}
      style={{ minHeight: "100vh", display: "flex", flexDirection: "column", background: "var(--bg-page)" }}
    >
      <Header />
      <main style={{ flex: 1 }}>
        <Routes>
          <Route path="/login"          element={<PublicRoute><Login /></PublicRoute>} />
          <Route path="/"               element={<UnderConstructionSplash />} />
          <Route path="/signup"         element={<UnderConstructionSplash />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/profile"       element={<ProtectedRoute><Profile /></ProtectedRoute>} />
          <Route path="/dashboard"      element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
          <Route path="/sample-lookup"  element={<AdminSampleLookupRoute><SampleLookupPage /></AdminSampleLookupRoute>} />
          <Route path="/admin"          element={<AdminRoute><AdminDashboard /></AdminRoute>} />
          <Route path="/admin/learning-lab" element={<AdminRoute><AdminDashboard /></AdminRoute>} />
          <Route path="*"               element={<UnderConstructionSplash />} />
        </Routes>
      </main>
    </div>
  );
}

// ── Root ──────────────────────────────────────────────────────

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <ThemeProvider>
          <AppShell />
        </ThemeProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}
